import React, { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, TrendingUp, TrendingDown, Sparkles, RefreshCw, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { rassegne, articles, tierLabels, contentTypeLabels, dashboardKPIs, coverageByTier, sentimentOverTime, topSources, spokespersonMentions, executiveSummary, sentimentLabels } from '../lib/data';
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-sentiment-positive';
    case 'neutral':
      return 'bg-sentiment-neutral';
    case 'negative':
      return 'bg-sentiment-negative';
    default:
      return 'bg-muted';
  }
}
function getTierColor(tier: number | string) {
  switch (tier) {
    case 1:
      return 'bg-tier-1 text-white';
    case 2:
      return 'bg-tier-2 text-white';
    case 3:
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted';
  }
}
function KPI({
  label,
  value,
  trend,
  accent = false
}: {
  label: string;
  value: string | number;
  trend?: number;
  accent?: boolean;
}) {
  return <div className="flex flex-col gap-2" data-id="element-906">
      <div className="text-sm text-muted-foreground font-normal" data-id="element-907">{label}</div>
      <div className={`${`text-3xl md:text-4xl font-light leading-none ${accent ? 'text-accent' : 'text-foreground'}`} gap-[0px]`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-908">
        {value}
      </div>
      {trend !== undefined && <div className="flex items-center gap-1.5 text-sm bg-[#FFFFFF] font-normal" data-id="element-909">
          {trend > 0 ? <TrendingUp className="h-3.5 w-3.5 text-sentiment-positive" data-id="element-910" /> : <TrendingDown className="h-3.5 w-3.5 text-sentiment-negative" data-id="element-911" />}
          <span className={trend > 0 ? 'text-sentiment-positive' : 'text-sentiment-negative'} data-id="element-912">
            {trend > 0 ? '+' : ''}
            {trend}%
          </span>
          <span className="text-muted-foreground" data-id="element-913">vs prec.</span>
        </div>}
    </div>;
}
export function RassegnaDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const rassegna = rassegne.find(r => r.id === id) || rassegne[0];
  const rassegnaArticles = articles;
  const topArticles = articles.filter(a => a.status === 'approved').slice(0, 5);
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-[0px]" data-id="element-914">
      {/* Header + Tabs as one white block */}
      <div className="bg-card border-b border-border" data-id="element-915">
        <div className="px-6 pt-6 md:pt-8 max-w-[1400px] mx-auto w-full" data-id="element-916">
          <button onClick={() => navigate('/rassegne')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-id="element-917">
            <ArrowLeft className="h-3.5 w-3.5" data-id="element-918" />
            Rassegne
          </button>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-919">
            <div className="min-w-0" data-id="element-920">
              <h1 className="text-xl md:text-2xl font-normal tracking-tight leading-tight mb-2" data-id="element-921">
                {rassegna.name}
              </h1>
              <div className="text-sm text-muted-foreground font-normal" data-id="element-922">
                {rassegna.dateRange}
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-923">
              <Button variant="outline" className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent h-11 px-6 text-sm" data-id="element-924">
                <Share2 className="h-4 w-4 md:mr-2" data-id="element-925" />
                <span className="hidden md:inline" data-id="element-926">Condividi</span>
              </Button>
              <Button className="rounded-full h-11 px-6 text-sm" data-id="element-927">
                <Download className="h-4 w-4 md:mr-2" data-id="element-928" />
                <span className="hidden md:inline" data-id="element-929">Esporta PDF</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar - sticky */}
      <div className="border-b border-border sticky top-0 z-10 bg-card" data-id="element-930">
        <div className="px-6 max-w-[1400px] mx-auto w-full flex items-end gap-0" data-id="element-931">
          {[{
          value: 'overview',
          label: 'Panoramica'
        }, {
          value: 'analysis',
          label: 'Analisi'
        }, {
          value: 'articles',
          label: `Articoli`,
          count: rassegna.articleCount
        }].map(tab => <button key={tab.value} onClick={() => setActiveTab(tab.value)} className="relative px-5 py-3 text-sm transition-colors" style={{
          backgroundColor: activeTab === tab.value ? '#FFFFFF' : 'transparent',
          color: activeTab === tab.value ? 'var(--foreground)' : 'var(--muted-foreground)',
          fontWeight: activeTab === tab.value ? 500 : 400
        }} data-id="element-932">
              {tab.label}
              {tab.count !== undefined && <span className="ml-2 text-xs" style={{
            color: 'var(--muted-foreground)'
          }} data-id="element-933">
                  {tab.count}
                </span>}
              {activeTab === tab.value && <span className="absolute bottom-0 left-0 right-0" style={{
            height: '2px',
            backgroundColor: '#0187c3'
          }} data-id="element-934" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div data-id="element-935">
        <TabsContent value="overview" className="m-0 p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-936">
          {/* KPI strip */}
          <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-id="element-937">
            <KPI label="Articoli totali" value={dashboardKPIs.totalArticles.value} trend={dashboardKPIs.totalArticles.trend} data-id="element-938" />
            <KPI label="Score medio" value={dashboardKPIs.avgRelevanceScore.value.toFixed(1)} accent data-id="element-939" />
            <KPI label="AVE totale" value={`€${(dashboardKPIs.totalAVE.value / 1000).toFixed(0)}k`} trend={dashboardKPIs.totalAVE.trend} data-id="element-940" />
            <div className="flex flex-col gap-2" data-id="element-941">
              <div className="text-sm text-muted-foreground font-normal" data-id="element-942">
                Sentiment
              </div>
              <div className="flex items-baseline gap-2 leading-none" data-id="element-943">
                <span className="text-2xl md:text-3xl font-light text-sentiment-positive" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-944">
                  {dashboardKPIs.sentimentMix.positive}
                </span>
                <span className="text-sm text-muted-foreground font-light" data-id="element-945">
                  /
                </span>
                <span className="text-xl text-muted-foreground font-light" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-946">
                  {dashboardKPIs.sentimentMix.neutral}
                </span>
                <span className="text-sm text-muted-foreground font-light" data-id="element-947">
                  /
                </span>
                <span className="text-xl text-sentiment-negative font-light" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-948">
                  {dashboardKPIs.sentimentMix.negative}
                </span>
              </div>
              <div className="text-sm text-muted-foreground font-normal" data-id="element-949">
                Positivi / neutri / negativi
              </div>
            </div>
          </section>

          {/* Executive Summary */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-950">
            <div className="border-l-2 border-accent pl-5 md:pl-6" data-id="element-951">
              <div className="flex items-center justify-between mb-3" data-id="element-952">
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal" data-id="element-953">
                  <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-954" />
                  <span data-id="element-955">Riepilogo AI</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-956">
                  <Button variant="ghost" size="sm" className="h-9 px-3.5 text-sm text-muted-foreground" data-id="element-957">
                    <RefreshCw className="h-3.5 w-3.5 md:mr-1.5" data-id="element-958" />
                    <span className="hidden md:inline" data-id="element-959">Rigenera</span>
                  </Button>
                  <Button variant="ghost" size="sm" className="h-9 px-3.5 text-sm text-muted-foreground" data-id="element-960">
                    <Edit3 className="h-3.5 w-3.5 md:mr-1.5" data-id="element-961" />
                    <span className="hidden md:inline" data-id="element-962">Modifica</span>
                  </Button>
                </div>
              </div>
              <p className="text-base leading-relaxed text-foreground" data-id="element-963">
                {executiveSummary}
              </p>
            </div>
          </section>

          {/* Top Articles */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-964">
            <div className="flex items-baseline justify-between mb-5" data-id="element-965">
              <h2 className="text-lg font-medium" data-id="element-966">Top articoli</h2>
            </div>
            <div className="space-y-1" data-id="element-967">
              {topArticles.map((article, i) => <Fragment key={article.id} data-id="element-968">
                  {i > 0 && <Separator data-id="element-969" />}
                  <button onClick={() => navigate(`/article/${article.id}`)} className="w-full text-left py-4 md:py-5 group hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors" data-id="element-970">
                    <div className="flex items-start gap-4" data-id="element-971">
                      <img src={article.sourceLogo} alt={article.source} className="w-9 h-9 rounded flex-shrink-0 mt-0.5" data-id="element-972" />
                      <div className="flex-1 min-w-0" data-id="element-973">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs text-muted-foreground font-normal" data-id="element-974">
                          <Badge className={`text-[10px] px-1.5 py-0 border-0 font-normal ${getTierColor(article.tier)}`} data-id="element-975">
                            {tierLabels[article.tier]}
                          </Badge>
                          <span data-id="element-976">{article.source}</span>
                          <span data-id="element-977">·</span>
                          <span data-id="element-978">
                            {new Date(article.date).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short'
                        })}
                          </span>
                        </div>
                        <h3 className="font-normal text-sm md:text-base leading-snug mb-2 line-clamp-2" data-id="element-979">
                          {article.title}
                        </h3>
                        <div className="flex items-center gap-5 text-sm font-normal" data-id="element-980">
                          <div className="flex items-baseline gap-1.5" data-id="element-981">
                            <span className="font-light text-foreground" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-982">
                              {article.relevanceScore.toFixed(1)}
                            </span>
                            <span className="text-xs text-muted-foreground font-normal" data-id="element-983">
                              score
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1.5 text-muted-foreground" data-id="element-984">
                            <span style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-985">
                              €{(article.ave / 1000).toFixed(1)}k
                            </span>
                            <span className="text-xs" data-id="element-986">AVE</span>
                          </div>
                          <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} title={sentimentLabels[article.sentiment]} data-id="element-987" />
                        </div>
                      </div>
                    </div>
                  </button>
                </Fragment>)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="analysis" className="m-0 p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-988">
          {/* Sentiment over time */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-989">
            <h3 className="text-base font-medium mb-6" data-id="element-990">Sentiment nel tempo</h3>
            <div className="h-64" data-id="element-991">
              <ResponsiveContainer width="100%" height="100%" data-id="element-992">
                <AreaChart data={sentimentOverTime} margin={{
                top: 5,
                right: 0,
                bottom: 0,
                left: -20
              }} data-id="element-993">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-994" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 12
                }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-995" />
                  <YAxis tick={{
                  fontSize: 12
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-996" />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-997" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-998" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-999" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1000">
            {/* Coverage by Tier */}
            <section className="bg-card border border-border rounded-lg p-6" data-id="element-1001">
              <h3 className="text-base font-medium mb-6" data-id="element-1002">Copertura per tier</h3>
              <div className="space-y-4" data-id="element-1003">
                {coverageByTier.map(item => <div key={item.tier} data-id="element-1004">
                    <div className="flex items-center justify-between mb-2 text-sm" data-id="element-1005">
                      <span className="text-foreground font-normal" data-id="element-1006">
                        {item.tier}
                      </span>
                      <span className="text-muted-foreground font-normal" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1007">
                        {item.count}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1008">
                      <div className="h-full bg-foreground/80 rounded-full" style={{
                    width: `${item.percentage}%`
                  }} data-id="element-1009" />
                    </div>
                  </div>)}
              </div>
            </section>

            {/* Top Sources */}
            <section className="bg-card border border-border rounded-lg p-6" data-id="element-1010">
              <h3 className="text-base font-medium mb-6" data-id="element-1011">Top testate</h3>
              <div className="space-y-4" data-id="element-1012">
                {topSources.map(source => <div key={source.name} className="flex items-center justify-between text-sm" data-id="element-1013">
                    <span className="text-foreground font-normal truncate pr-4" data-id="element-1014">
                      {source.name}
                    </span>
                    <div className="flex items-center gap-4 text-muted-foreground flex-shrink-0 font-normal" data-id="element-1015">
                      <span style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1016">
                        {source.articles} art.
                      </span>
                      <span className="text-foreground font-light w-8 text-right" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1017">
                        {source.avgScore.toFixed(1)}
                      </span>
                    </div>
                  </div>)}
              </div>
            </section>
          </div>

          {/* Spokespersons */}
          <section className="bg-card border border-border rounded-lg p-6" data-id="element-1018">
            <h3 className="text-base font-medium mb-6" data-id="element-1019">Portavoce citati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1020">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between text-sm p-4 border border-border rounded-md bg-muted/10" data-id="element-1021">
                  <div className="min-w-0 pr-4" data-id="element-1022">
                    <div className="text-foreground font-normal truncate" data-id="element-1023">
                      {person.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-normal" data-id="element-1024">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1025">
                    <span className="font-light text-lg" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1026">
                      {person.mentions}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-1027" />
                  </div>
                </div>)}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="articles" className="m-0 p-6 max-w-[1400px] mx-auto" data-id="element-1028">
          <div className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-1029">
            {/* Mobile */}
            <div className="md:hidden divide-y divide-border" data-id="element-1030">
              {rassegnaArticles.map(article => <button key={article.id} onClick={() => navigate(`/article/${article.id}`)} className="w-full text-left py-5 hover:bg-muted/30 px-4 transition-colors" data-id="element-1031">
                  <div className="flex items-center gap-2 mb-1.5 text-sm text-muted-foreground font-normal" data-id="element-1032">
                    <img src={article.sourceLogo} alt="" className="w-4 h-4 rounded-sm" data-id="element-1033" />
                    <span data-id="element-1034">{article.source}</span>
                    <span data-id="element-1035">·</span>
                    <span data-id="element-1036">
                      {new Date(article.date).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'short'
                  })}
                    </span>
                  </div>
                  <h3 className="font-normal text-base leading-snug mb-2 line-clamp-2" data-id="element-1037">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm font-normal" data-id="element-1038">
                    <span className="font-light" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1039">
                      {article.relevanceScore.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground font-normal" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1040">
                      €{(article.ave / 1000).toFixed(1)}k
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} data-id="element-1041" />
                  </div>
                </button>)}
            </div>

            {/* Desktop */}
            <div className="hidden md:block" data-id="element-1042">
              <Table data-id="element-1043">
                <TableHeader data-id="element-1044">
                  <TableRow className="hover:bg-transparent" data-id="element-1045">
                    <TableHead className="w-[100px] text-sm" data-id="element-1046">Data</TableHead>
                    <TableHead className="text-sm" data-id="element-1047">Testata</TableHead>
                    <TableHead className="w-[90px] text-sm" data-id="element-1048">Tier</TableHead>
                    <TableHead className="text-sm" data-id="element-1049">Titolo</TableHead>
                    <TableHead className="w-[150px] text-sm" data-id="element-1050">
                      Tipologia
                    </TableHead>
                    <TableHead className="text-right w-[80px] text-sm" data-id="element-1051">
                      Score
                    </TableHead>
                    <TableHead className="text-right w-[80px] text-sm" data-id="element-1052">
                      AVE
                    </TableHead>
                    <TableHead className="w-[60px]" data-id="element-1053"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-1054">
                  {rassegnaArticles.map(article => <TableRow key={article.id} className="cursor-pointer font-normal" onClick={() => navigate(`/article/${article.id}`)} data-id="element-1055">
                      <TableCell className="text-[15px] text-muted-foreground whitespace-nowrap py-4 font-normal" data-id="element-1056">
                        {new Date(article.date).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell className="py-4 font-normal" data-id="element-1057">
                        <div className="flex items-center gap-2" data-id="element-1058">
                          <img src={article.sourceLogo} alt="" className="w-5 h-5 rounded-sm" data-id="element-1059" />
                          <span className="text-[15px]" data-id="element-1060">{article.source}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4" data-id="element-1061">
                        <Badge className={`text-[10px] px-1.5 py-0 border-0 font-normal ${getTierColor(article.tier)}`} data-id="element-1062">
                          {tierLabels[article.tier]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[400px] py-4" data-id="element-1063">
                        <div className="text-[15px] font-normal truncate" data-id="element-1064">
                          {article.title}
                        </div>
                      </TableCell>
                      <TableCell className="text-[15px] text-muted-foreground py-4 font-normal" data-id="element-1065">
                        {contentTypeLabels[article.contentType]}
                      </TableCell>
                      <TableCell className="text-right font-light text-[15px] py-4" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1066">
                        {article.relevanceScore.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right text-[15px] text-muted-foreground py-4" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1067">
                        €{(article.ave / 1000).toFixed(1)}k
                      </TableCell>
                      <TableCell className="py-4" data-id="element-1068">
                        <div className={`w-2 h-2 rounded-full ${getSentimentColor(article.sentiment)} mx-auto`} data-id="element-1069" />
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>;
}